import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContainer,
  Heading,
  Divider,
  Content,
  Item,
  ListBox,
  Text,
  Flex,
  ProgressCircle,
} from '@adobe/react-spectrum';
import { Folder, SocialNetwork } from '../icons/s2';
import type { Selection } from '@react-types/shared';
import { Site } from '../../types';

export interface SitePickerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sites: Site[];
  loading?: boolean;
  onSelect: (site: Site) => void;
}

export const SitePickerDialog: React.FC<SitePickerDialogProps> = ({
  isOpen,
  onOpenChange,
  sites,
  loading = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  return (
    <DialogContainer onDismiss={() => onOpenChange(false)} isDismissable>
      {isOpen && (
        <Dialog size="S">
          <Heading>{t('components.sitePickerDialog.title')}</Heading>
          <Divider />
          <Content>
            {loading ? (
              <Flex justifyContent="center" alignItems="center" marginY="size-400">
                <ProgressCircle aria-label={t('components.sitePickerDialog.loading')} isIndeterminate size="S" />
              </Flex>
            ) : sites.length === 0 ? (
              <Flex justifyContent="center" marginY="size-300">
                <Text>
                  {t('components.sitePickerDialog.empty')}
                </Text>
              </Flex>
            ) : (
              <ListBox
                aria-label={t('components.sitePickerDialog.listAria')}
                maxHeight="size-4000"
                selectionMode="single"
                onSelectionChange={(keys: Selection) => {
                  if (keys === 'all') return;
                  const [key] = Array.from(keys);
                  const selectedSite = sites.find((site) => site.site_id === String(key));
                  if (!selectedSite) return;
                  onSelect(selectedSite);
                  onOpenChange(false);
                }}
              >
                {sites.map((site) => (
                  <Item key={site.site_id} textValue={site.site_name}>
                    {site.domain_type === 'orchestrator' ? <SocialNetwork size="S" /> : <Folder size="S" />}
                    <Text>{site.site_name}</Text>
                  </Item>
                ))}
              </ListBox>
            )}
          </Content>
        </Dialog>
      )}
    </DialogContainer>
  );
};
